/**
 * Calendar-day arithmetic on `YYYY-MM-DD` keys — no domain knowledge, no
 * timezone baked in (except where you pass one).
 *
 * These operate on whole calendar days, not instants: a "day" here is a date on
 * a wall calendar, so DST and clock time never enter into it. Callers that need
 * "today in our operating timezone" pass the zone to {@link todayYmdInZone} and
 * keep the rest of the maths zone-free.
 */

/** `YYYY-MM-DD`, shifted by whole UTC days. Input may carry a time — it's dropped. */
export function addUtcDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate.slice(0, 10)}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Shift a `YYYY-MM-DD` key by whole days. Parses the fields directly rather
 * than through `Date` string parsing so it never depends on the host timezone.
 */
export function shiftYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return dt.toISOString().slice(0, 10);
}

/**
 * Whole calendar days from `fromYmd` to `toYmd` (`to − from`). Positive when
 * `toYmd` is later. `null` if either key is not `YYYY-MM-DD`.
 */
export function daysBetweenYmd(fromYmd: string, toYmd: string): number | null {
  const ymd = /^\d{4}-\d{2}-\d{2}/;
  if (!ymd.test(fromYmd) || !ymd.test(toYmd)) return null;
  const from = Date.parse(`${fromYmd.slice(0, 10)}T00:00:00.000Z`);
  const to = Date.parse(`${toYmd.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86_400_000);
}

/**
 * The `YYYY-MM-DD` calendar day for `asOf` in the given IANA timezone
 * (e.g. `"Europe/London"`). Use this once, at the edge, then do the rest of the
 * arithmetic with the zone-free helpers above.
 */
export function todayYmdInZone(timeZone: string, asOf: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(asOf);
}
